import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { addToCart, addToCartAsync, updateCartItemAsync, removeFromCartAsync, updateQuantity, removeFromCart } from '../features/cart/cartSlice';
import ProductReviews from './ProductReviews';
import StarRating from './StarRating';
import Header from './Header';
import placeholderImage from '../images/Kurti1.avif';
import { API_URL, BASE_URL, BROKEN_IMAGE_PLACEHOLDER, getImageUrl as getImageUrlFromConfig } from '../config';
import { isYouTubeUrl, getYouTubeWatchUrl } from '../utils/productHelpers';
import './ProductDetailPage.css';

const getImageUrl = (url, fallback) => getImageUrlFromConfig(url, fallback);

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const userRole = useSelector((state) => state.auth.userRole);
  const cartItems = useSelector((state) => state.cart.items);

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ratingSummary, setRatingSummary] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedColorId, setSelectedColorId] = useState(null);
  const [selectedSizeId, setSelectedSizeId] = useState(null);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [isImageZoomed, setIsImageZoomed] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });

  useEffect(() => {
    const raw = detail?.variants;
    const arr = Array.isArray(raw) ? raw : (raw && Array.isArray(raw.$values) ? raw.$values : []);
    if (arr.length) {
      const firstInStock = arr.find((variant) => Number(variant.stock) > 0) || arr[0];
      setSelectedColorId((prev) => (prev == null ? firstInStock.colorId ?? null : prev));
      setSelectedSizeId((prev) => (prev == null ? firstInStock.sizeId ?? null : prev));
    }
  }, [detail?.productId, detail?.variants]);

  // Set default selected seller when detail loads
  useEffect(() => {
    const sellersRaw = detail?.sellers;
    const sellersArr = Array.isArray(sellersRaw) ? sellersRaw : (sellersRaw && Array.isArray(sellersRaw.$values) ? sellersRaw.$values : []);
    if (sellersArr.length > 0 && !selectedSeller) {
      setSelectedSeller(sellersArr[0]);
    }
  }, [detail?.sellers, selectedSeller]);

  const refetchRatingSummary = React.useCallback(async () => {
    const productId = detail?.productId;
    if (!productId) return;
    try {
      const summaryRes = await axios.get(`${API_URL}/Reviews/product/${productId}/summary`);
      setRatingSummary(summaryRes.data);
    } catch {
      setRatingSummary((prev) => prev || { averageRating: 0, totalReviews: 0 });
    }
  }, [detail?.productId]);

  useEffect(() => {
    const fetchDetail = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${API_URL}/Products/detail/${id}`);
        setDetail(response.data);
        const productId = response.data.productId;
        try {
          const summaryRes = await axios.get(`${API_URL}/Reviews/product/${productId}/summary`);
          setRatingSummary(summaryRes.data);
        } catch {
          setRatingSummary({ averageRating: 0, totalReviews: 0 });
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Product not found.');
        } else {
          setError('Failed to load product.');
        }
        setDetail(null);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [id]);

  const images = useMemo(() => {
    const raw = detail?.images;
    const arr = Array.isArray(raw) ? raw : (raw && Array.isArray(raw.$values) ? raw.$values : []);
    if (!arr.length) return [];
    return [...arr].sort((a, b) => (a.displayOrder - b.displayOrder) || (a.imageId - b.imageId));
  }, [detail?.images]);

  useEffect(() => {
    setIsImageZoomed(false);
    setZoomOrigin({ x: 50, y: 50 });
  }, [detail?.productId, selectedImageIndex]);

  const variants = useMemo(() => {
    const raw = detail?.variants;
    if (Array.isArray(raw)) return raw;
    if (raw && Array.isArray(raw.$values)) return raw.$values;
    return [];
  }, [detail?.variants]);

  const variantsById = useMemo(() => {
    const map = new Map();
    variants.forEach((variant) => {
      map.set(variant.variantId, variant);
      map.set(Number(variant.variantId), variant);
    });
    return map;
  }, [variants]);

  const colorStockById = useMemo(() => {
    const map = new Map();
    variants.forEach((variant) => {
      if (variant.colorId == null) return;
      map.set(variant.colorId, (map.get(variant.colorId) || 0) + (Number(variant.stock) || 0));
    });
    return map;
  }, [variants]);

  const colors = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(variants)) return [];
    variants.forEach((v) => {
      if (v.colorId == null) return;
      if (!map.has(v.colorId)) {
        map.set(v.colorId, {
          colorId: v.colorId,
          colorName: v.colorName || 'Color',
          hexCode: v.hexCode,
        });
      }
    });
    return Array.from(map.values());
  }, [variants]);

  const sizes = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(variants)) return [];
    variants.forEach((v) => {
      if (v.sizeId != null && v.sizeName) map.set(v.sizeId, { sizeId: v.sizeId, sizeName: v.sizeName, sizeCategory: v.sizeCategory });
    });
    return Array.from(map.values());
  }, [variants]);

  // Show all uploaded images and product video in one media gallery.
  const displayImages = images;
  const displayMedia = useMemo(() => {
    const imageMedia = displayImages.map((img, idx) => {
      const variant = variantsById.get(img.variantId) || variantsById.get(Number(img.variantId));
      const colorId = variant?.colorId ?? null;
      const colorStock = colorId == null ? null : (colorStockById.get(colorId) || 0);
      return {
        type: 'image',
        key: `img-${img.imageId ?? idx}`,
        imageUrl: img.imageUrl,
        colorId,
        outOfStock: colorStock != null && colorStock <= 0,
      };
    });
    const rawVideoUrl = detail?.videoUrl;
    if (rawVideoUrl) {
      if (isYouTubeUrl(rawVideoUrl)) {
        const thumbRaw = detail?.videoThumbnailUrl;
        imageMedia.push({
          type: 'youtube',
          key: 'video-main',
          youtubeWatchUrl: getYouTubeWatchUrl(rawVideoUrl),
          thumbnailUrl: thumbRaw
            ? getImageUrl(thumbRaw, placeholderImage)
            : getImageUrl(detail?.imageUrl, placeholderImage),
        });
      } else {
        imageMedia.push({
          type: 'video',
          key: 'video-main',
          videoUrl: getImageUrl(rawVideoUrl),
        });
      }
    }
    return imageMedia;
  }, [displayImages, detail?.videoUrl, detail?.videoThumbnailUrl, detail?.imageUrl, variantsById, colorStockById]);

  const selectedVariant = useMemo(() => {
    if (!variants.length) return null;
    return variants.find(
      (v) =>
        (selectedColorId == null || v.colorId === selectedColorId) &&
        (selectedSizeId == null || v.sizeId === selectedSizeId)
    ) || variants.find((v) => v.colorId === selectedColorId) || variants.find((v) => v.sizeId === selectedSizeId) || variants[0];
  }, [variants, selectedColorId, selectedSizeId]);

  // When a seller is selected, show that seller's price and stock; otherwise use variant/product
  const displayPrice = selectedSeller?.sellerPrice != null
    ? Number(selectedSeller.sellerPrice)
    : (selectedVariant ? selectedVariant.price : detail?.price ?? 0);
  const displayOriginalPrice = selectedVariant?.originalPrice ?? detail?.originalPrice ?? null;
  const variantStock = selectedVariant != null
    ? Number(selectedVariant.stock) || 0
    : Number(detail?.stock ?? 0);
  const sellerStock = selectedSeller?.stockQuantity != null ? Number(selectedSeller.stockQuantity) : null;
  const displayStock = sellerStock != null ? Math.min(sellerStock, variantStock) : variantStock;
  const discountPercent =
    displayOriginalPrice != null && displayOriginalPrice > displayPrice && displayOriginalPrice > 0
      ? Math.round(((displayOriginalPrice - displayPrice) / displayOriginalPrice) * 100)
      : null;

  const colorCards = useMemo(() => (
    colors.map((color) => {
      const imageForColor = images.find((image) => {
        if (image.variantId == null) return false;
        const variant = variantsById.get(image.variantId) || variantsById.get(Number(image.variantId));
        return variant?.colorId === color.colorId;
      });
      const stock = colorStockById.get(color.colorId) || 0;
      const outOfStock = stock <= 0;

      return {
        ...color,
        outOfStock,
        imageUrl: imageForColor?.imageUrl
          ? getImageUrl(imageForColor.imageUrl, placeholderImage)
          : getImageUrl(detail?.imageUrl, placeholderImage),
      };
    })
  ), [colorStockById, colors, detail?.imageUrl, images, variantsById]);

  const selectedMedia = displayMedia[selectedImageIndex] || null;
  const selectedColorOutOfStock = selectedColorId != null && (colorStockById.get(selectedColorId) || 0) <= 0;
  const selectedMediaOutOfStock = Boolean(selectedMedia?.outOfStock) || selectedColorOutOfStock;
  const mainImageUrl = useMemo(() => {
    if (selectedMedia?.type === 'image' && selectedMedia.imageUrl) {
      return getImageUrl(selectedMedia.imageUrl, placeholderImage);
    }
    return getImageUrl(detail?.imageUrl, placeholderImage);
  }, [detail?.imageUrl, selectedMedia]);
  const isSelectedMediaVideo = selectedMedia?.type === 'video';
  const isSelectedMediaYoutube = selectedMedia?.type === 'youtube';
  const isSelectedMediaPlayable = isSelectedMediaVideo || isSelectedMediaYoutube;

  const openYoutubeVideo = (watchUrl) => {
    if (watchUrl) {
      window.open(watchUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Get quantity of current product in cart
  const getCartQuantity = () => {
    if (!detail) return 0;
    const variantId = selectedVariant?.variantId ?? selectedVariant?.VariantId ?? null;
    const toId = (value) => {
      if (value == null || value === '') return null;
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    };
    const item = cartItems.find((item) =>
      toId(item.productId ?? item.ProductId) === toId(detail.productId)
      && toId(item.variantId ?? item.VariantId) === toId(variantId)
    );
    return item ? Number(item.quantity || 0) : 0;
  };

  const stockLimitMessage = () => {
    const colorName = selectedVariant?.colorName;
    const sizeName = selectedVariant?.sizeName;
    const variantLabel = [colorName, sizeName].filter(Boolean).join(' ');
    if (displayStock <= 0) {
      return variantLabel ? `No stock available for ${variantLabel}.` : 'This item is out of stock.';
    }
    return variantLabel
      ? `Only ${displayStock} in stock for ${variantLabel}.`
      : `Only ${displayStock} in stock.`;
  };

  const handleAddToCart = () => {
    if (!detail) return;
    if (variants.length > 0 && !selectedVariant) {
      window.alert('Please select a color and size.');
      return;
    }
    if (getCartQuantity() >= displayStock) {
      window.alert(stockLimitMessage());
      return;
    }
    const selectedColorImage = colorCards.find((card) => card.colorId === selectedColorId)?.imageUrl
      || mainImageUrl;
    const cartData = {
      productId: detail.productId,
      productName: detail.productName,
      price: displayPrice,
      originalPrice: displayOriginalPrice ?? null,
      imageUrl: selectedColorImage,
      sellerName: selectedSeller?.sellerName ?? null,
      sellerCode: selectedSeller?.sellerCode ?? null,
      productSellerId: selectedSeller?.productSellerId ?? null,
      deliveryCharge: selectedSeller?.isFreeDelivery ? 0 : Number(selectedSeller?.deliveryCharge || 0),
      variantId: selectedVariant?.variantId ?? null,
      colorName: selectedVariant?.colorName ?? null,
      sizeName: selectedVariant?.sizeName ?? null,
      stock: displayStock,
    };

    if (isLoggedIn) {
      dispatch(addToCartAsync({
        productId: detail.productId,
        quantity: 1,
        price: displayPrice,
        originalPrice: displayOriginalPrice ?? undefined,
        productSellerId: selectedSeller?.productSellerId ?? undefined,
        variantId: selectedVariant?.variantId ?? selectedVariant?.VariantId ?? undefined,
        colorName: selectedVariant?.colorName ?? undefined,
        sizeName: selectedVariant?.sizeName ?? undefined,
      })).unwrap().catch((err) => {
        const message = typeof err === 'string'
          ? err
          : err?.message || 'Could not add this size to cart.';
        window.alert(message);
      });
    } else {
      dispatch(addToCart(cartData));
    }
  };

  const handleIncrement = () => {
    if (!detail) return;
    const currentQty = getCartQuantity();
    if (currentQty >= displayStock) {
      window.alert(stockLimitMessage());
      return;
    }
    const variantId = selectedVariant?.variantId ?? null;
    if (isLoggedIn) {
      dispatch(updateCartItemAsync({ productId: detail.productId, quantity: currentQty + 1, variantId }));
    } else {
      dispatch(updateQuantity({ productId: detail.productId, quantity: currentQty + 1, variantId, stock: displayStock }));
    }
  };

  const handleRemoveFromCart = () => {
    if (!detail) return;
    const variantId = selectedVariant?.variantId ?? null;
    if (isLoggedIn) {
      dispatch(removeFromCartAsync({ productId: detail.productId, variantId }));
    } else {
      dispatch(removeFromCart({ productId: detail.productId, variantId }));
    }
  };

  const handleImageMouseEnter = () => {
    setIsImageZoomed(true);
  };

  const handleImageMouseMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    setZoomOrigin({
      x: Math.min(100, Math.max(0, x)),
      y: Math.min(100, Math.max(0, y)),
    });
  };

  const handleImageMouseLeave = () => {
    setIsImageZoomed(false);
    setZoomOrigin({ x: 50, y: 50 });
  };

  useEffect(() => {
    setSelectedImageIndex(0);
  }, [selectedColorId]);

  useEffect(() => {
    if (selectedImageIndex >= displayMedia.length) {
      setSelectedImageIndex(0);
    }
  }, [displayMedia.length, selectedImageIndex]);

  if (loading) {
    return (
      <div className="product-detail-page">
        <div className="product-detail-loading">Loading product...</div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="product-detail-page">
        <div className="product-detail-error">
          <p>{error || 'Product not found.'}</p>
          <button type="button" onClick={() => navigate('/')}>Back to Home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="product-detail-page">
      <Header />
      <div className="product-detail-container">
        <button type="button" className="back-link" onClick={() => navigate(-1)}>
          ← Back
        </button>

        <div className="product-detail-main">
          <div className="product-detail-header">
            {/* Image gallery */}
            <div className="product-image-gallery">
              <div className="thumbnail-strip">
                {displayMedia.length ? (
                  displayMedia.map((media, idx) => (
                    <button
                      key={media.key}
                      type="button"
                      className={`thumbnail ${selectedImageIndex === idx ? 'active' : ''} ${media.type === 'youtube' ? 'thumbnail-youtube' : ''} ${media.outOfStock ? 'out-of-stock' : ''}`}
                      onClick={() => {
                        if (media.type === 'youtube') {
                          openYoutubeVideo(media.youtubeWatchUrl);
                          return;
                        }
                        setSelectedImageIndex(idx);
                      }}
                    >
                      {media.type === 'youtube' ? (
                        <>
                          <img
                            src={media.thumbnailUrl}
                            alt=""
                            onError={(e) => { e.target.src = BROKEN_IMAGE_PLACEHOLDER; }}
                          />
                          <span className="thumbnail-video-indicator thumbnail-video-play" aria-hidden="true">▶</span>
                        </>
                      ) : media.type === 'video' ? (
                        <div className="thumbnail-video-indicator">▶</div>
                      ) : (
                        <img src={getImageUrl(media.imageUrl, placeholderImage)} alt="" onError={(e) => { e.target.src = BROKEN_IMAGE_PLACEHOLDER; }} />
                      )}
                      {media.outOfStock && media.type === 'image' && (
                        <span className="image-oos-badge">OOS</span>
                      )}
                    </button>
                  ))
                ) : (
                  <div className="thumbnail placeholder active">
                    <img src={getImageUrl(detail.imageUrl, placeholderImage)} alt="" onError={(e) => { e.target.src = BROKEN_IMAGE_PLACEHOLDER; }} />
                  </div>
                )}
              </div>
              <div
                className={`main-image-wrap ${!isSelectedMediaPlayable && isImageZoomed ? 'is-zoomed' : ''} ${isSelectedMediaYoutube ? 'is-youtube' : ''} ${selectedMediaOutOfStock ? 'out-of-stock' : ''}`}
                onMouseEnter={!isSelectedMediaPlayable ? handleImageMouseEnter : undefined}
                onMouseMove={!isSelectedMediaPlayable ? handleImageMouseMove : undefined}
                onMouseLeave={!isSelectedMediaPlayable ? handleImageMouseLeave : undefined}
              >
                {isSelectedMediaYoutube ? (
                  <button
                    type="button"
                    className="product-detail-youtube-link"
                    onClick={() => openYoutubeVideo(selectedMedia.youtubeWatchUrl)}
                    aria-label="Watch product video on YouTube"
                  >
                    <img
                      src={selectedMedia.thumbnailUrl}
                      alt={detail.productName}
                      className="product-detail-image"
                      onError={(e) => { e.target.src = BROKEN_IMAGE_PLACEHOLDER; }}
                    />
                    <span className="product-detail-video-play-hint" aria-hidden="true">
                      <span className="product-detail-video-play-icon">▶</span>
                    </span>
                  </button>
                ) : isSelectedMediaVideo ? (
                  <video
                    src={selectedMedia.videoUrl}
                    controls
                    playsInline
                    preload="metadata"
                    className="product-detail-video"
                  />
                ) : (
                  <img
                    src={mainImageUrl}
                    alt={detail.productName}
                    className="product-detail-image"
                    style={{ transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%` }}
                    onError={(e) => { e.target.src = BROKEN_IMAGE_PLACEHOLDER; }}
                  />
                )}
                {selectedMediaOutOfStock && (
                  <span className="image-oos-badge">Out of stock</span>
                )}
              </div>
            </div>

            <div className="product-detail-info">
              {detail.brandName && (
                <Link
                  to={`/brand/${encodeURIComponent(detail.brandName)}`}
                  state={{ brandImageUrl: getImageUrl(detail.brandImageUrl) }}
                  className="brand-link"
                >
                  Visit the {detail.brandName} Store
                </Link>
              )}
              <h1>{detail.productName}</h1>
              {ratingSummary && (
                <div className="product-rating-summary">
                  <StarRating rating={Math.round(ratingSummary.averageRating)} readonly size="medium" />
                  <span className="rating-text">
                    {ratingSummary.averageRating?.toFixed(1) ?? '0.0'} ({ratingSummary.totalReviews ?? 0} reviews)
                  </span>
                  <button
                    type="button"
                    className="reviews-link"
                    onClick={() => {
                      document.getElementById('reviews')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  >
                    See all {ratingSummary.totalReviews ?? 0} reviews
                  </button>
                </div>
              )}

              {/* Pricing */}
              <div className="pricing-block">
                {discountPercent != null && discountPercent > 0 && (
                  <span className="discount-badge">-{discountPercent}%</span>
                )}
                <span className="current-price">₹{Number(displayPrice).toFixed(2)}</span>
                {displayOriginalPrice != null && displayOriginalPrice > displayPrice && (
                  <span className="original-price">₹{Number(displayOriginalPrice).toFixed(2)}</span>
                )}
                <p className="tax-note">Inclusive of all taxes.</p>
              </div>

              {/* Selected Seller Info */}
              {selectedSeller && (
                <div className="selected-seller-info">
                  <span className="sold-by-label">Sold by:</span>
                  <span className="sold-by-seller">{selectedSeller.sellerName}</span>
                  {selectedSeller.sellerCode && (
                    <span className="sold-by-code">Seller ID: {selectedSeller.sellerCode}</span>
                  )}
                  {selectedSeller.isFreeDelivery ? (
                    <span className="seller-delivery-badge free">FREE Delivery</span>
                  ) : (
                    <span className="seller-delivery-badge">Delivery ₹{selectedSeller.deliveryCharge?.toFixed(2)}</span>
                  )}
                  <span className="seller-delivery-date">Get it by {selectedSeller.deliveryDate}</span>
                </div>
              )}

              {/* Available sizes & colors summary (from product variants) */}
              {(colors.length > 0 || sizes.length > 0) && (
                <div className="available-attributes-summary">
                  {colors.length > 0 && sizes.length > 0 && (
                    <span>Available in <strong>{sizes.length} size{sizes.length !== 1 ? 's' : ''}</strong> and <strong>{colors.length} color{colors.length !== 1 ? 's' : ''}</strong>. Select below.</span>
                  )}
                  {colors.length > 0 && sizes.length === 0 && (
                    <span>Available in <strong>{colors.length} color{colors.length !== 1 ? 's' : ''}</strong>. Select below.</span>
                  )}
                  {colors.length === 0 && sizes.length > 0 && (
                    <span>Available in <strong>{sizes.length} size{sizes.length !== 1 ? 's' : ''}</strong>. Select below.</span>
                  )}
                </div>
              )}

              <div className="variant-options">
                {colors.length > 0 && (
                  <div className="option-block">
                    <span className="option-label">Colour: {colors.find((c) => c.colorId === selectedColorId)?.colorName ?? 'Select'}</span>
                    <div className="color-variant-grid">
                      {colorCards.map((c) => (
                        <button
                          key={c.colorId}
                          type="button"
                          className={`color-variant-option ${selectedColorId === c.colorId ? 'selected' : ''} ${c.outOfStock ? 'out-of-stock' : ''}`}
                          title={c.outOfStock ? `${c.colorName} (out of stock)` : c.colorName}
                          aria-pressed={selectedColorId === c.colorId}
                          aria-disabled={c.outOfStock}
                          onClick={() => {
                            setSelectedColorId(c.colorId);
                            const imageIndex = displayMedia.findIndex((media) => media.colorId === c.colorId);
                            if (imageIndex >= 0) setSelectedImageIndex(imageIndex);
                          }}
                        >
                          <span className="color-variant-photo">
                            <img src={c.imageUrl} alt={c.colorName} />
                            {c.outOfStock && <span className="color-oos-overlay">OOS</span>}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {sizes.length > 0 && (
                  <div className="option-block">
                    <span className="option-label">Size: {sizes.find((s) => s.sizeId === selectedSizeId)?.sizeName ?? 'Select'}</span>
                    <div className="size-options">
                      {sizes.map((s) => {
                        const variantForSize = variants.find(
                          (v) => v.sizeId === s.sizeId && (selectedColorId == null || v.colorId === selectedColorId)
                        );
                        const outOfStock = !variantForSize || Number(variantForSize.stock) <= 0;
                        return (
                          <button
                            key={s.sizeId}
                            type="button"
                            className={`size-btn ${selectedSizeId === s.sizeId ? 'selected' : ''} ${outOfStock ? 'out-of-stock' : ''}`}
                            disabled={outOfStock}
                            onClick={() => setSelectedSizeId(s.sizeId)}
                          >
                            {s.sizeName}
                          </button>
                        );
                      })}
                    </div>
                    <Link to="#size-chart" className="size-chart-link">Size Chart</Link>
                    <p className="product-detail-stock">
                      {displayStock > 0 ? `In stock (${displayStock} available)` : 'Out of stock'}
                    </p>
                  </div>
                )}
              </div>

              {sizes.length === 0 && (
                <p className="product-detail-stock">
                  {displayStock > 0 ? `In stock (${displayStock} available)` : 'Out of stock'}
                </p>
              )}
              
              {getCartQuantity() > 0 ? (
                <div className="cart-actions-detail">
                  <div className="quantity-selector-detail">
                    <button 
                      type="button"
                      className="qty-btn delete-btn" 
                      onClick={handleRemoveFromCart}
                      title="Remove from cart"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                    <span className="qty-value">{getCartQuantity()}</span>
                    <button 
                      type="button"
                      className="qty-btn add-btn" 
                      onClick={handleIncrement}
                      title="Add more"
                      disabled={getCartQuantity() >= displayStock}
                    >
                      +
                    </button>
                  </div>
                  <button 
                    type="button"
                    className="go-to-cart-btn-detail"
                    onClick={() => navigate('/cart')}
                  >
                    Go to Cart →
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="add-to-cart-button"
                  onClick={handleAddToCart}
                  disabled={displayStock <= 0 || userRole === 'Admin'}
                >
                  Add to Cart
                </button>
              )}
            </div>
          </div>

          {/* Product details / Specifications */}
          {(() => {
            const specsRaw = detail?.specifications;
            const specs = Array.isArray(specsRaw) ? specsRaw : (specsRaw && Array.isArray(specsRaw.$values) ? specsRaw.$values : []);
            return (specs.length > 0 || (userRole !== 'Seller' && (detail?.shortDescription || detail?.description))) && (
            <div className="product-details-section">
              <h2>Product Details</h2>
              {specs.length > 0 && (
                <table className="spec-table">
                  <tbody>
                    {specs.map((spec, idx) => (
                      <tr key={idx}>
                        <td className="spec-name">{spec.attributeName}</td>
                        <td className="spec-value">{spec.attributeValue}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {(userRole !== 'Seller' && (detail.shortDescription || detail.description)) && (
                <>
                  <h3>About this item</h3>
                  {detail.shortDescription && (
                    <ul className="about-list">
                      <li>{detail.shortDescription}</li>
                    </ul>
                  )}
                  {detail.description && (
                    <div
                      className="product-description-content"
                      dangerouslySetInnerHTML={{ __html: detail.description }}
                    />
                  )}
                </>
              )}
            </div>
          );
          })()}

          {/* Size Chart & Measurement Guide (Clothes/Garments) */}
          {/* Size Chart & Measurement Guide (Clothes/Garments) — customer-facing copy only */}
          {(detail.sizeChartContent || detail.measurementGuide) && (
            <div id="size-chart" className="size-chart-section">
              <h2>Size Chart &amp; Measurement Guide</h2>
              <p className="size-chart-intro">Use the chart and measurement tips below to choose the right size.</p>
              {detail.sizeChartContent && (
                <div
                  className="size-chart-content"
                  dangerouslySetInnerHTML={{ __html: detail.sizeChartContent }}
                />
              )}
              {detail.measurementGuide && (
                <div
                  className="measurement-guide-content"
                  dangerouslySetInnerHTML={{ __html: detail.measurementGuide }}
                />
              )}
            </div>
          )}

          <div id="reviews">
            <ProductReviews productId={detail.productId} onRatingUpdated={refetchRatingSummary} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
